import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MarkerAction } from '../types/marker';
import { Category } from '../types/category';
import { ACTION_STATUS, ActionStatus, TIMEOUTS } from '../config/constants';
import { useAppContext } from '../context/AppContext';
import { ActionType } from '../types/action';

export const useMarkerActions = () => {
  const { dispatch: _dispatch } = useAppContext();

  const createAction = useCallback((description: string, type: ActionType = 'other', isSystem: boolean = false): MarkerAction => ({
    id: uuidv4(),
    markerId: '', // Sera rempli par le service lors de la création/mise à jour du marqueur
    date: new Date(),
    type,
    description,
    status: ACTION_STATUS.COMPLETED as ActionStatus,
    is_system: isSystem
  }), []);

  const createInitialAction = useCallback(() => 
    createAction('Création du marqueur', 'create' as ActionType, true), // Action système
  [createAction]);
  
  const createUpdateAction = useCallback((field?: string) => {
    if (field) {
      return createAction(`Modification du champ: ${field}`, 'update' as ActionType, true); // Action système
    }
    return createAction('Modification du marqueur', 'update' as ActionType, true); // Action système
  }, [createAction]);

  const createCustomAction = useCallback((type: ActionType, details?: string) => {
    // Pour les actions de type 'update', ne pas inclure le type dans la description
    const description = details ? 
      (type === 'update' ? details : `${type} - ${details}`) : 
      type;
    return createAction(description, type, false); // Action personnalisée
  }, [createAction]);

  const createCategoryChangeAction = useCallback((oldCategory: Category | undefined | string, newCategory: Category | undefined | string) => {
    const oldCategoryName = typeof oldCategory === 'string' ? oldCategory : oldCategory?.name || 'Sans catégorie';
    const newCategoryName = typeof newCategory === 'string' ? newCategory : newCategory?.name || 'Sans catégorie';
    return createAction(`${oldCategoryName} → ${newCategoryName}`, 'category_change' as ActionType, true); // Action système
  }, [createAction]);

  const shouldAddAction = useCallback((existingActions: MarkerAction[], newAction: MarkerAction): boolean => {
    if (!existingActions) return true;
    
    const lastAction = existingActions[existingActions.length - 1];
    
    if (lastAction && lastAction.type === newAction.type) {
      const timeDiff = new Date().getTime() - new Date(lastAction.date).getTime();
      return timeDiff >= TIMEOUTS.ACTION_GROUPING;
    }

    return true;
  }, []);

  const mergeActions = useCallback((existingActions: MarkerAction[] = [], newAction: MarkerAction): MarkerAction[] => {
    if (shouldAddAction(existingActions, newAction)) {
      return [...existingActions, newAction];
    }
    return existingActions;
  }, [shouldAddAction]);

  return {
    createAction,
    createInitialAction,
    createUpdateAction,
    createCustomAction,
    createCategoryChangeAction,
    shouldAddAction,
    mergeActions
  };
};

